import React, { useState, useEffect } from 'react';
import { SessionProvider, useSession } from '../contexts/SessionContext';
import { NotificationProvider } from '../contexts/NotificationContext';
import SessionGuard from '../components/session/SessionGuard';
import SessionStatus from '../components/session/SessionStatus';
import AdminLogin from '../features/admin/AdminLogin';
import AdminSidebar from '../features/admin/AdminSidebar';
import AdminNavbar from '../features/admin/AdminNavbar';
import AdminDashboard from '../features/admin/AdminDashboard';
import AdminVendors from '../features/admin/AdminVendors';
import AdminPayouts from '../features/admin/AdminPayouts';
import AdminInventoryManagement from '../features/admin/AdminInventoryManagement';
import AdminProducts from '../features/admin/AdminProducts';
import AdminCategoriesManagement from '../features/admin/AdminCategoriesManagement';
import AdminKYCReview from '../features/admin/AdminKYCReview';
import AdminPricing from '../features/admin/AdminPricing';
import AdminOrders from '../features/admin/AdminOrders';
import AdminPackages from '../features/admin/AdminPackages';
import AdminDeliveryManagement from '../features/admin/AdminDeliveryManagement';
import AdminDeliveryTracking from '../features/admin/AdminDeliveryTracking';
import AdminDeliveryRadius from '../features/admin/AdminDeliveryRadius';
import AdminServiceAreas from '../features/admin/AdminServiceAreas';
import AdminCustomerManagement from '../features/admin/AdminCustomerManagement';
import AdminSettings from '../features/admin/AdminSettings';
import Reports from '../features/admin/AdminPayoutReports';
import AdminCoupons from '../features/admin/AdminCoupons';
import AdminAds from '../features/admin/AdminAds';
import './App.css';

const AppContent = () => {
  const { isAuthenticated, user, login, logout } = useSession();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Track viewport to detect mobile
  useEffect(() => {
    const updateIsMobile = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth <= 768);
      }
    };
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  // Lock body scroll when sidebar is open on mobile
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const shouldLock = isMobile && !sidebarCollapsed;
      document.body.style.overflow = shouldLock ? 'hidden' : '';
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
    };
  }, [isMobile, sidebarCollapsed]);

  const handleAdminLogin = (adminData) => {
    login({
      email: adminData.email,
      type: 'admin'
    });
  };

  const renderAdminContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <AdminDashboard />;
      case 'vendors':
        return <AdminVendors />;
      case 'inventory':
        return <AdminInventoryManagement />;
      case 'pricing':
        return <AdminPricing />;
      case 'kyc':
        return <AdminKYCReview />;
      case 'products':
        return <AdminProducts />;
      case 'categories':
        return <AdminCategoriesManagement />;
      case 'orders':
        return <AdminOrders />;
      case 'packages':
        return <AdminPackages />;
      case 'deliveries':
        return <AdminDeliveryManagement />;
      case 'delivery-radius':
        return <AdminDeliveryRadius />;
      case 'service-areas':
        return <AdminServiceAreas />;
      case 'delivery-tracking':
        return <AdminDeliveryTracking />;
      case 'customers':
        return <AdminCustomerManagement />;
      case 'coupons':
        return <AdminCoupons />;
      case 'ads':
        return <AdminAds />;
      case 'reports':
        return <Reports />;
      case 'payouts':
        return <AdminPayouts />;
      case 'settings':
        return <AdminSettings />;
      default:
        return <AdminDashboard />;
    }
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleAdminLogin} />;
  }

  // Render admin portal
  if (user?.type === 'admin') {
    return (
      <SessionGuard requiredUserType="admin">
        <div className="app">
          <AdminSidebar 
            activeTab={activeTab} 
            setActiveTab={setActiveTab}
            collapsed={sidebarCollapsed}
            setCollapsed={setSidebarCollapsed}
            onLogout={logout}
            isMobile={isMobile}
          />
          {isMobile && !sidebarCollapsed && (
            <div
              className="mobile-overlay active"
              onClick={() => setSidebarCollapsed(true)}
            />
          )}
          <div className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            <AdminNavbar 
              activeTab={activeTab} 
              setActiveTab={setActiveTab} 
              userEmail={user.email}
              onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
              sidebarCollapsed={sidebarCollapsed}
            >
              <SessionStatus />
            </AdminNavbar>
            <div className="content">
              {renderAdminContent()}
            </div>
          </div>
        </div>
      </SessionGuard>
    );
  }

  // If authenticated but not admin, show login
  return <AdminLogin onLogin={handleAdminLogin} />;
}

function App() {
  return (
    <SessionProvider>
      <NotificationProvider>
        <AppContent />
      </NotificationProvider>
    </SessionProvider>
  );
}

export default App;

